import * as React from 'react';

/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {
    ITelemetryContent,
    Module,
    Node,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject
} from '@msdyn365-commerce-modules/utilities';

import { ISignInItem, ISignInLoading, ISignInLocalAccount, ISignInViewProps } from '@msdyn365-commerce-modules/account-management';

import { ISignInConfig, ISignInProps, ISignInResources } from '../definition-extensions/sign-in.ext.props.autogenerated';

interface ISignInConfigCustom extends ISignInConfig {
    includeCloudflareZarazScript?: boolean;
    showContinueAsGuestButton?: boolean;
    showVclubColumn?: boolean;
}

interface ISignInPropsCustom extends ISignInProps<ISignInConfigCustom> {
    config: ISignInConfigCustom;
}

const LocalAccount: React.FC<ISignInLocalAccount & { resources: ISignInResources }> = ({
    localAccount,
    items,
    forgetPassword,
    disclaimer,
    error,
    signInButton,
    resources
}) => {
    const [password, setPassword] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);

    return (
        <Node {...localAccount}>
            {items &&
                items.map((item: ISignInItem) => {
                    if (item.key === 'password') {
                        return (
                            <div className='ms-sign-in__account-item ms-sign-in__account-item-password entry-item attrEntry'>
                                <label id='password_label' className='ms-sign-in__account-item-label'>
                                    {resources.passwordLabelText}
                                </label>
                                <div className='input-group'>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        id='password'
                                        className='ms-sign-in__account-item-input ms-sign-in__account-item-password'
                                        aria-required='true'
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                    />
                                    <button
                                        onClick={() => setShowPassword(curState => !curState)}
                                        className='input-group__icon-button'
                                        type='button'
                                    >
                                        <i
                                            aria-label={showPassword ? resources.hidePasswordLabel : resources.showPasswordLabel}
                                            className={`input-group__icon ${
                                                showPassword ? 'input-group__icon--eye-off' : 'input-group__icon--eye'
                                            }`}
                                        />
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <Node key={item.key} {...item.wrapper}>
                            {item.label}
                            {item.error}
                            {item.input}
                        </Node>
                    );
                })}
            {forgetPassword}
            {disclaimer}
            {error}
            {signInButton}
        </Node>
    );
};

const SignInLoading: React.FC<ISignInLoading> = ({ modal, modalBody, icon, message }) => {
    return (
        <Node {...modal}>
            <Node {...modalBody}>
                {icon}
                {message}
            </Node>
        </Node>
    );
};

const SignInView: React.FC<ISignInViewProps & ISignInPropsCustom> = props => {
    const {
        signIn,
        viewState,
        loading,
        defaultAADConainer,
        aadConainer,
        signInSection,
        signUpSection,
        signInLocalAccount,
        signUpLink,
        config
    } = props;
    const canonicalDomain = props.signIn.moduleProps.context.request.canonicalDomain;
    const sitePath =
        props.signIn.moduleProps.context.request.sitePath && props.signIn.moduleProps.context.request.sitePath !== null
            ? props.signIn.moduleProps.context.request.sitePath
            : '';
    const requestUrl: string = `https://${canonicalDomain}${sitePath}`;

    const telemetryContent: ITelemetryContent = getTelemetryObject(
        props.context.request.telemetryPageName!,
        props.friendlyName,
        props.telemetry
    );
    const payLoad = getPayloadObject('click', telemetryContent!, props.resources.signUpButtonText);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);

    const showContinueAsGuest = props.config.showContinueAsGuestButton;
    const showVclubColumn = props.config.showVclubColumn;

    React.useEffect(() => {
        if (!viewState.isShowLoading) {
            const registerButtonLink = document.getElementById('createAccount');
            const vclubButtonLink = document.getElementById('vclub-signin');

            if (registerButtonLink && vclubButtonLink) {
                // @ts-ignore
                vclubButtonLink.href = registerButtonLink.href;
            }
        }
    }, [viewState.isShowLoading]);

    return (
        <Module {...signIn}>
            {config.includeCloudflareZarazScript && <script src='https://zaraz.venchi.com/cdn-cgi/zaraz/i.js'></script>}
            {viewState.isShowLoading && <Node {...defaultAADConainer} />}
            {viewState.isShowLoading && <SignInLoading {...loading} />}
            <Node {...aadConainer}>
                <div className='mobile-anchor-container col-12'>
                    <a href="#sign-in-section">{props.resources.signInLabel}</a>
                    <a href="#sign-up-section">{props.resources.signUpButtonText}</a>
                </div>
                <Node {...signInSection} className='col-12 col-lg-4' id='sign-in-section'>
                    <h1 className='mobile-hide'>{props.resources.signInLabel}</h1>
                    <div className={signInSection.className}>
                        {props.resources.signInHeading && (
                            <h2 className='ms-sign-in__sign-up-heading'>{props.resources.signInHeading}</h2>
                        )}
                        {props.resources.signInDescriptionText && (
                            <p className='ms-sign-in__sign-in-description'>{props.resources.signInDescriptionText}</p>
                        )}
                        {LocalAccount({ ...signInLocalAccount, resources: props.resources })}
                    </div>
                </Node>
                {showVclubColumn && (
                    <Node className={`col-12 col-lg-4 ms-sign-in__vclub-section`}>
                        <h2 className='ms-sign-in__sign-up-heading'>{props.resources.signinVclubTitle}</h2>
                        <p className='ms-sign-in__sign-up-description'>{props.resources.signinVclubText}</p>
                        <div>
                            <a
                                id='vclub-signin'
                                className='ms-sign-in__sign-up-link msc-btn'
                                href='#'
                                aria-label={props.resources.signUpButtonText}
                                {...attributes}
                            >
                                {props.resources.signUpButtonText}
                            </a>
                        </div>

                        {showContinueAsGuest && (
                            <div>
                                <a className={'msc-cart__btn-guestcheckout'} href={`${requestUrl}/checkout`}>
                                    {props.resources.continueAsGuest ?? 'Continue as guest'}
                                </a>
                            </div>
                        )}
                    </Node>
                )}
                <Node {...signUpSection} className='col-12 col-lg-4' id='sign-up-section'>
                    <h1 className='mobile-hide'>{props.resources.signUpButtonText}</h1>
                    <div className={signUpSection.className}>
                        <div id='signUpHeading'>
                            {props.resources.signUpHeading && (
                                <h2 className='ms-sign-in__sign-up-heading'>{props.resources.signUpHeading}</h2>
                            )}
                            {props.resources.signUpDescriptionText && (
                                <p className='ms-sign-in__sign-up-description'>{props.resources.signUpDescriptionText}</p>
                            )}
                        </div>
                        <div className='ms-sign-in__sign-up-actions'>
                            {signUpLink}

                            {showContinueAsGuest && (
                                <a className={'msc-cart__btn-guestcheckout'} href={`${requestUrl}/checkout`}>
                                    {props.resources.continueAsGuest ?? 'Continue as guest'}
                                </a>
                            )}
                        </div>
                    </div>
                </Node>
            </Node>
        </Module>
    );
};

export default SignInView;
